import { css } from '@emotion/css';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { ReactComponent as ReactionIcon } from '../../assets/images/reaction/img_reaction_button.svg';
import { RootState } from '../../features';
import { actions } from '../../features/chat';
import { sendEvent } from '../../lib/google-analytics';
import { BackButton } from '../shared/buttons/BackButton';
import { HintButton } from '../shared/buttons/HintButton';
import { DialogRoute } from '../shared/routes/DialogRoute';
import { ChatErrorDialog } from './ChatErrorDialog';
import { ChatForm } from './ChatForm';
import { ChatHintDialog } from './ChatHintDialog';
import { ChatWarnings } from './ChatWarnings';
import { PreviousMessageButton } from './PreviousMessageButton';

const chatStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #e6ec8d;
  color: #4d3906;

  header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 52px;
    padding: 0 4px;
  }
  h1 {
    font-size: 16px;
  }
  .body {
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    padding: 8px 12px;
    overflow-x: hidden;
  }
  .buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .reaction-link {
    width: 44px;
    height: 44px;
    padding: 0;
    margin: 10px 6px;
  }
  .reaction-link:active {
    transform: scale(0.9);
    transition: transform 0.15s cubic-bezier(0.17, 0.89, 0.32, 1.49);
  }
  .space {
    width: 44px;
    height: 44px;
    margin: 10px 0;
  }
`;

const Chat: React.FC = () => {
  const { t } = useTranslation(['chat', 'translation']);
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const { sentLatest, errorTypes } = useSelector((state: RootState) => state.chat);
  const { unlockFlags } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const hintPath = routeMatch ? `${routeMatch.url}/hint` : undefined;
  const errorDialogPath = routeMatch ? `${routeMatch.url}/error` : undefined;

  useEffect(() => {
    return () => {
      dispatch(actions.reset());
    };
  }, [dispatch]);

  const handleSubmit = useCallback(
    (message: string) => {
      const type = 'keyboard';
      dispatch(actions.postMessage({ message, type }));
      sendEvent('chat_send_message', 'submit');
    },
    [dispatch]
  );

  const handleHintClick = useCallback(() => {
    if (hintPath) {
      history.push(hintPath);
    }
  }, [history, hintPath]);

  const handleLatestMessageClick = useCallback(() => {
    dispatch(actions.fillPrevMessage());
    sendEvent('chat_resend_message', 'click');
  }, [dispatch]);

  const handleButtonClick = () => {
    history.push('/players/reaction');
    sendEvent('chat_reaction_link', 'click');
  };

  const shouldShowReactionLink = unlockFlags?.isDisplayEmoticon;

  if (history.location.pathname === '/players/chat' && errorTypes.length > 0) {
    return (
      <Redirect
        push={true}
        to={{ pathname: '/players/chat/error', search: `?types=${errorTypes.join(',')}` }}
      />
    );
  }

  return (
    <>
      <main className={chatStyles}>
        <header>
          <BackButton ariaLabel={t('translation:aria_button_back_to_home')} />
          <h1>{t('top')}</h1>
          <HintButton ariaLabel={t('aria_help')} onClick={handleHintClick} />
        </header>
        <div className="body">
          <ChatForm onSubmit={handleSubmit} />
          <div className="buttons">
            {sentLatest ? (
              <PreviousMessageButton message={sentLatest} onClick={handleLatestMessageClick} />
            ) : (
              <div className="space" />
            )}
            {shouldShowReactionLink && (
              <button
                className="reaction-link"
                aria-label={t('aria_to_reaction')}
                onClick={handleButtonClick}
              >
                <ReactionIcon />
              </button>
            )}
          </div>
          <ChatWarnings />
        </div>
      </main>

      <DialogRoute path={hintPath}>
        <ChatHintDialog />
      </DialogRoute>
      <DialogRoute path={errorDialogPath}>
        <ChatErrorDialog />
      </DialogRoute>
    </>
  );
};

export default Chat;
